'use client'

/**
 * Mobile listing fold actions, beside the price, and the P09 next step for
 * every arrival (lib/listing/listing-next-step): Ask about this home, Get
 * price-change alerts for this home, Walk through it, then the similar rail
 * and the community market. Reuses the existing Sarah, contact, price-watch,
 * and book flows. Not a sticky/fixed bar (Matt lock).
 */
import { useCallback, useEffect, useId, useState, useTransition } from 'react'
import { submitListingPriceDropWatch } from '@/lib/analytics/submit-alert-signup'
import {
  alertNextStepLabel,
  askNextStepLabel,
  listingArrival,
  listingNextStepParams,
  type ListingArrival,
  type ListingNextStepCta,
  type ListingNextStepLinks,
} from '@/lib/listing/listing-next-step'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { readRrSessionId, trackEvent } from '@/lib/tracking'

/** Opens Sarah when her launcher is mounted and closed; false sends the click on to the contact form. */
function openSarah(): boolean {
  if (typeof document === 'undefined') return false
  const launcher = document.querySelector<HTMLButtonElement>('button[data-sarah="launcher"]')
  if (!launcher || launcher.getAttribute('aria-expanded') !== 'false') return false
  launcher.click()
  return true
}

export function ListingFoldActions({
  listingKey,
  addressLine,
  askHref,
  walkHref,
  kind = 'home',
  nextStep,
  communitySlug = null,
}: {
  listingKey: string
  addressLine: string
  askHref: string
  walkHref: string
  /** UX review F26: a lease or a lot is not a home to walk through. */
  kind?: 'home' | 'lease' | 'land'
  /** P09 onward links: the similar rail on this page and the community (or town) market. */
  nextStep?: ListingNextStepLinks
  communitySlug?: string | null
}) {
  const [watchOpen, setWatchOpen] = useState(false)
  const [arrival, setArrival] = useState<ListingArrival>('other')
  const home = addressLine.trim() || 'this home'

  useEffect(() => {
    setArrival(listingArrival(document.referrer, window.location.search))
  }, [])

  const params = (cta: ListingNextStepCta) => listingNextStepParams({ cta, listingKey, communitySlug, arrival })

  return (
    <div className="listing-face__fold" id="listing-fold-actions" data-arrival={arrival}>
      <a
        className="listing-face__fold-action"
        href={askHref}
        onClick={(event) => {
          trackEvent('ask_question_click', params('ask'))
          if (openSarah()) event.preventDefault()
        }}
      >
        {askNextStepLabel(kind)}
      </a>
      <a
        className="listing-face__fold-action"
        href="#close"
        onClick={(event) => {
          event.preventDefault()
          trackEvent('click_cta', params('price_alert'))
          setWatchOpen(true)
        }}
      >
        {alertNextStepLabel(kind)}
      </a>
      <a
        className="listing-face__fold-action listing-face__fold-action--primary"
        href={kind === 'lease' ? askHref : walkHref}
        onClick={() => trackEvent('schedule_tour_click', params('tour'))}
      >
        {kind === 'lease' ? 'Ask about this property' : kind === 'land' ? 'Walk the lot' : 'Walk through it'}
      </a>
      {nextStep?.similar || nextStep?.market ? (
        <ul className="listing-face__fold-next" aria-label="More around this home">
          {nextStep.similar ? (
            <li>
              <a
                href={nextStep.similar.href}
                data-next-step="similar"
                onClick={() => trackEvent('view_similar_listings', params('similar'))}
              >
                {nextStep.similar.label}
              </a>
            </li>
          ) : null}
          {nextStep.market ? (
            <li>
              <a
                href={nextStep.market.href}
                data-next-step="market"
                onClick={() => trackEvent('view_community', params('market'))}
              >
                {nextStep.market.label}
              </a>
            </li>
          ) : null}
        </ul>
      ) : null}
      <ListingWatchPriceSheet
        listingKey={listingKey}
        addressLine={home}
        open={watchOpen}
        onOpenChange={setWatchOpen}
      />
    </div>
  )
}

function ListingWatchPriceSheet({
  listingKey,
  addressLine,
  open,
  onOpenChange,
}: {
  listingKey: string
  addressLine: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const uid = useId().replace(/:/g, '')
  const [email, setEmail] = useState('')
  const [trap, setTrap] = useState('')
  const [state, setState] = useState<'idle' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [pending, start] = useTransition()

  const submit = useCallback(() => {
    setError(null)
    start(async () => {
      const res = await submitListingPriceDropWatch({
        email,
        listingKey,
        addressLine,
        company: trap,
        sessionId: readRrSessionId(),
      })
      if (res.ok) setState('done')
      else setError(res.error)
    })
  }, [email, listingKey, addressLine, trap])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" overlayClassName="bg-foreground/50">
        <SheetHeader>
          <SheetTitle>Price-change alerts for this home</SheetTitle>
          <SheetDescription>
            {state === 'done'
              ? `We are watching the price on ${addressLine}.`
              : 'One email per price change on this home. Nothing else, and you can unsubscribe any time.'}
          </SheetDescription>
        </SheetHeader>
        {state === 'done' ? (
          <p className="px-4 text-sm text-muted-foreground">
            One email per price change on this home, and nothing else. Unsubscribe any time from any of
            them.
          </p>
        ) : (
          <form
            data-clarity-mask="True"
            className="flex flex-col gap-3 px-4"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <p className="text-sm text-muted-foreground">
              Sellers move on price more often than they move on anything else. Leave your email and we
              will tell you the day this one changes.
            </p>
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${uid}-watch-email`}>Your email</Label>
              <Input
                id={`${uid}-watch-email`}
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="sr-only" aria-hidden="true">
              <Label htmlFor={`${uid}-watch-company`}>Company</Label>
              <Input
                id={`${uid}-watch-company`}
                name="company"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={trap}
                onChange={(event) => setTrap(event.target.value)}
              />
            </div>
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            <SheetFooter>
              <Button type="submit" disabled={pending}>
                {pending ? 'Setting it up…' : 'Get price-change alerts'}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  )
}
